import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/cn";
import { routes } from "@/routes";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronRightIcon } from "@/components/ui/icons";
import { PROFILE_NAV_ITEMS } from "@/components/layout/profileNavItems";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";

/**
 * Moldura das telas de perfil: menu lateral fixo + conteúdo.
 *
 * O card "Lista de produtos comprados última semana" fica aqui porque aparece
 * em todas as abas do perfil.
 */
export function ProfileLayout() {
  const { isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size={28} className="text-brand" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Guarda o destino para voltar depois do login.
    return (
      <Navigate to={routes.login} state={{ from: location.pathname }} replace />
    );
  }

  return (
    <div className="mx-auto grid max-w-[1200px] gap-6 px-4 py-6 md:grid-cols-[220px_minmax(0,1fr)]">
      {/*
        `min-w-0` é o que impede o menu de esticar a coluna: sem ele o item do
        grid adota a largura natural das abas e a página inteira transborda no
        celular. Até `md` as abas viram só ícones, numa linha só — o nome delas
        aparece no submenu de "Seu perfil", no menu sanduíche.
      */}
      <nav
        aria-label="Menu da conta"
        className="min-w-0 md:sticky md:top-20 md:self-start"
      >
        <ul className="flex gap-1 md:flex-col md:gap-0.5">
          {PROFILE_NAV_ITEMS.map(
            ({ to, label, icon: Icon, end, desktopOnly }) => (
              <li key={to} className={cn(desktopOnly && "hidden md:block")}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 whitespace-nowrap rounded px-3 py-2.5 text-sm transition-colors",
                      isActive
                        ? // Sem rótulo, a cor sozinha marca pouco qual aba está
                          // aberta; o fundo resolve isso no celular.
                          "bg-black/[0.06] font-semibold text-ink md:bg-transparent"
                        : "text-ink-soft hover:bg-black/[0.04] hover:text-ink",
                    )
                  }
                >
                  <Icon className="size-5 shrink-0" />
                  <span className="sr-only md:not-sr-only">{label}</span>
                </NavLink>
              </li>
            ),
          )}
        </ul>
      </nav>

      <div className="min-w-0">
        <header className="mb-5">
          <h1 className="text-[20px] font-semibold leading-tight text-ink lg:text-[26px]">
            Informações do seu perfil
          </h1>
          <p className="mt-1 text-sm text-ink-soft">
            Você pode adicionar, alterar ou corrigir suas informações pessoais e
            dados da conta.
          </p>
        </header>

        <div className="flex max-w-[720px] flex-col gap-5">
          <LastWeekPurchasesLink />
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** Atalho para a lista de compras da semana, presente em todas as abas. */
function LastWeekPurchasesLink() {
  return (
    <Card>
      <NavLink
        to={routes.shoppingList}
        className="flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-surface-muted"
      >
        <span className="text-[15px] text-ink lg:text-[17px]">
          Ir para comprados na última semana
        </span>
        <ChevronRightIcon className="size-5 shrink-0 text-ink-soft" />
      </NavLink>
    </Card>
  );
}
